import React from 'react';

import {
  NPS_PRESET,
  OpinionScaleConfig,
  SCALE_END_LIMITS,
  createDefaultOpinionScaleConfig,
} from '../../CustomBlocks/scale/scaleTypes';
import {
  NumberStepper,
  SegmentedControl,
  SettingsGroup,
  SettingsRow,
  Switch,
} from '../slotBookingEditor/settingsUi';
import { TextButton } from '../slotBookingEditor/SlotBookingEditor.styles';
import { TextSetting } from '../quizBlocksEditor/quizEditorUi';

interface OpinionScaleEditorProps {
  config: OpinionScaleConfig;
  onChange: (patch: Partial<OpinionScaleConfig>) => void;
}

const START_OPTIONS: { value: '0' | '1'; label: string }[] = [
  { value: '0', label: 'Starts at 0' },
  { value: '1', label: 'Starts at 1' },
];

const OpinionScaleEditor: React.FC<OpinionScaleEditorProps> = ({ config, onChange }) => {
  const { start, end, lowLabel, midLabel, highLabel, colorCoded } = createDefaultOpinionScaleConfig();

  return (
    <>
      <SettingsGroup title="Presets">
        <SettingsRow label="NPS (0–10)" description="How likely are you to recommend us?">
          <TextButton type="button" onClick={() => onChange(NPS_PRESET)}>
            Apply
          </TextButton>
        </SettingsRow>
        <SettingsRow label="Agreement (1–5)" description="Disagree → Agree">
          <TextButton
            type="button"
            onClick={() => onChange({ start, end, lowLabel, midLabel, highLabel, colorCoded })}
          >
            Apply
          </TextButton>
        </SettingsRow>
      </SettingsGroup>

      <SettingsGroup title="Scale">
        <SettingsRow label="First number" stacked>
          <SegmentedControl
            value={String(config.start === 0 ? 0 : 1) as '0' | '1'}
            options={START_OPTIONS}
            onChange={(value) => onChange({ start: Number(value) })}
          />
        </SettingsRow>
        <SettingsRow label="Last number">
          <NumberStepper
            value={config.end}
            min={Math.max(SCALE_END_LIMITS.min, config.start + 1)}
            max={SCALE_END_LIMITS.max}
            step={1}
            unit=""
            label="Last number"
            onChange={(value) => onChange({ end: value })}
          />
        </SettingsRow>
        <SettingsRow label="Colour-coded" description="Red, amber and green, like NPS">
          <Switch
            checked={config.colorCoded}
            label="Colour-coded"
            onChange={(value) => onChange({ colorCoded: value })}
          />
        </SettingsRow>
      </SettingsGroup>

      <SettingsGroup title="Labels">
        <TextSetting
          label="Left"
          value={config.lowLabel}
          onChange={(value) => onChange({ lowLabel: value })}
        />
        <TextSetting
          label="Middle (optional)"
          value={config.midLabel}
          onChange={(value) => onChange({ midLabel: value })}
        />
        <TextSetting
          label="Right"
          value={config.highLabel}
          onChange={(value) => onChange({ highLabel: value })}
        />
      </SettingsGroup>

      <SettingsGroup footer="Multi-step forms only. Works best when this is the only question on its step.">
        <SettingsRow label="Go to next step on pick">
          <Switch
            checked={config.autoAdvance}
            label="Go to next step on pick"
            onChange={(autoAdvance) => onChange({ autoAdvance })}
          />
        </SettingsRow>
      </SettingsGroup>
    </>
  );
};

export default OpinionScaleEditor;
